import React from 'react';
import { computed, action, observable } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { IAppTabsConfiguration } from '../models';
import {
    ITabReference,
    ITabsProps,
} from '@kurtosys/ksys-app-components/dist/components/base/Tabs/models';
import { ITabComponent as ITabComponentBase } from '@kurtosys/ksys-app-components/dist/components/base/Tabs/models/ITabComponent';
import { ApplicationEmbed } from '@kurtosys/ksys-app-components/dist/components/base/ApplicationEmbed/ApplicationEmbed';
import { IApplicationEmbedInput } from '@kurtosys/ksys-app-components/dist/components/base/ApplicationEmbed/models';
import { IInputs } from '../../../models/app/IInputs';
import { TAppTabContent } from '../models/TAppTabContent';
import { IAppTabComponent } from '../models/IAppTabComponent';
import { AppStore } from '../../App/stores/AppStore';
import { utils, helpers } from '@kurtosys/ksys-app-template';

export class TabsStore extends StoreBase {
    static componentKey: 'tabs' = 'tabs';
    @observable activeTab: ITabReference | undefined;

    @computed
    get configuration(): IAppTabsConfiguration | undefined {
        if (this.storeContext && this.storeContext.appStore) {
            return this.storeContext.appStore.getComponentConfiguration(
                TabsStore.componentKey
            );
        }
    }

    @computed
    get tabInputs(): IApplicationEmbedInput[] {
        const { appStore } = this.storeContext;

        if (appStore && appStore.appParamsHelper) {
            const { appParamsHelper } = appStore;

            if (appParamsHelper.inputs) {
                const { inputs } = appParamsHelper;
                const inputKeys = Object.keys(inputs);

                return inputKeys.map((key: string) => {
                    return {
                        key,
                        value: inputs[key as keyof IInputs],
                    };
                });
            }
        }
        return [];
    }

    @computed
    get activeTabKey(): string | undefined {
        return this.activeTab && this.activeTab.key;
    }

    @computed
    get tabGroup(): string | undefined {
        const { appStore } = this.storeContext;
        const { appCardProps } = appStore;
        return appCardProps && appCardProps.title;
    }

    @computed
    get tabName(): string | undefined {
        const { label, key } = this.activeTab as ITabReference;
        return label || key;
    }

    @action
    onChange = (activeTab: ITabReference) => {
        this.activeTab = activeTab;
        const { appStore } = this.storeContext;
        const { dataLayerHelper } = appStore;
        if (!utils.typeChecks.isNullOrEmpty(appStore.urlRewrite)) {
            const { defaultUrl, base } = appStore.urlRewrite;
            const redirectHelper = new helpers.RedirectHelper(defaultUrl, base);
            const payload = {
                activeTab: this.activeTabKey || '',
            };
            const newUrl = redirectHelper.getRedirectPath(
                appStore.urlRewrite,
                payload
            );
            if (dataLayerHelper) {
                dataLayerHelper.logDataLayerEvent({
                    event: 'tab_click',
                    eventType: 'click',
                    context: {
                        tabName: this.tabName,
                        tabGroup: this.tabGroup,
                    },
                });
            }
            window.history.pushState({}, '', newUrl);
        }
    };

    orchestrateBreakPointProps(
        activeTab: string,
        children: IAppTabComponent[],
        rawConfig: IAppTabsConfiguration,
        appStore: AppStore
    ) {
        const props: ITabsProps = {
            ...rawConfig,
            onChange: this.onChange,
            active: activeTab,
            tabComponents: children.map((tabComponent: ITabComponentBase) => {
                tabComponent.content = tabComponent.content.map(
                    (contentItem: TAppTabContent, contentIndex: number) => {
                        return (
                            <div key={`app-embed-${contentIndex}`}>
                                <ApplicationEmbed
                                    hostname={appStore.serviceUrl}
                                    inputs={this.tabInputs}
                                    {...contentItem}
                                />
                            </div>
                        );
                    }
                );
                return tabComponent;
            }),
        };
        return props;
    }

    filterHiddenTabChildren(children: IAppTabComponent[]) {
        const filteredChildren: IAppTabComponent[] = [];

        children.forEach((child) => {
            const queriesProps = this.mergeQueriesAndProps(
                child
            ) as IAppTabComponent['props'];

            if (queriesProps) {
                if (!queriesProps.hide) {
                    filteredChildren.push(child);
                }
            }
        });

        return filteredChildren;
    }

    chooseActiveTab(filteredChildren: IAppTabComponent[], activeTab: string) {
        const { appStore } = this.storeContext;
        if (appStore.initialActiveTab) {
            activeTab = appStore.initialActiveTab;
        }
        // If the activeTab is being hidden, choose the next available tab
        if (
            utils.typeChecks.isNullOrUndefined(
                filteredChildren.find((child) => child.tabKey === activeTab)
            )
        ) {
            activeTab = filteredChildren[0] ? filteredChildren[0].tabKey : '';
        }

        return activeTab;
    }

    orchestratePropsAndQueries(children: IAppTabComponent[]) {
        const mergedChildren: IAppTabComponent[] = children.map((child) => {
            if (child.props || child.queries) {
                const queriesProps = this.mergeQueriesAndProps(
                    child
                ) as IAppTabComponent['props'];

                if (queriesProps) {
                    if (queriesProps.header) {
                        child.header = queriesProps.header;
                    }

                    if (queriesProps.tabKey) {
                        child.tabKey = queriesProps.tabKey;
                    }
                }
            }

            return child;
        });

        return mergedChildren;
    }

    @computed
    get tabsProps(): ITabsProps | undefined {
        const { appStore } = this.storeContext;
        const config = this.configuration;

        if (config) {
            if (config.breakpoints) {
                config.breakpoints = config.breakpoints.map((breakpoint) => {
                    const breakpointProps = breakpoint.props;

                    if (breakpointProps) {
                        let breakpointActiveTab =
                            breakpointProps.activeTab || '';
                        const breakpointChildren =
                            breakpointProps.children || [];
                        const filteredbreakpointChildren: IAppTabComponent[] =
                            this.filterHiddenTabChildren(breakpointChildren);
                        const mergeChildrenPropsAndQueries =
                            this.orchestratePropsAndQueries(
                                filteredbreakpointChildren
                            );
                        breakpointActiveTab = this.chooseActiveTab(
                            filteredbreakpointChildren,
                            breakpointActiveTab
                        );
                        breakpoint.props = this.orchestrateBreakPointProps(
                            breakpointActiveTab,
                            mergeChildrenPropsAndQueries,
                            breakpointProps,
                            appStore
                        );
                    }
                    return breakpoint;
                });
                return config;
            }

            let activeTab = config.activeTab || '';
            const children = config.children || [];
            const filteredChildren: IAppTabComponent[] =
                this.filterHiddenTabChildren(children);
            const mergeChildrenPropsAndQueries =
                this.orchestratePropsAndQueries(filteredChildren);
            activeTab = this.chooseActiveTab(filteredChildren, activeTab);
            return this.orchestrateBreakPointProps(
                activeTab,
                mergeChildrenPropsAndQueries,
                config,
                appStore
            );
        }

        return undefined;
    }

    @action
    async initialize(): Promise<void> {}
}
